import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NotificationEntry, type NotificationEntryHandle, NotificationInbox, productNames } from '@beyond-js/ui/react';
import { Inbox as Notifications } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { moved, Screen } from './screen';

/**
 * Follows an opened item's destination: a path of this interface stays in the application, anything
 * else is another product, reached by a full navigation, and a path here moves focus to the screen's
 * heading as every other way there does. The relay accepted the destination only after checking it is an
 * address of a product this installation serves.
 */
function useFollow() {
	const navigate = useNavigate();
	return useCallback(
		(destination: string) => {
			const url = new URL(destination, globalThis.location.href);
			if (url.origin === globalThis.location.origin) navigate(`${url.pathname}${url.search}${url.hash}`, { state: moved });
			else globalThis.location.assign(url.href);
		},
		[navigate]
	);
}

/**
 * The header entry: the bell, the unread count and the panel of recent items, which ends in the full
 * inbox. The count is read again whenever the person's read state changes here (`Inbox.listen`).
 */
export function Notices() {
	const { inbox, language } = usePresence();
	const follow = useFollow();
	const navigate = useNavigate();
	const entry = useRef<NotificationEntryHandle>(null);
	useEffect(() => inbox.listen(() => entry.current?.refresh()), [inbox]);
	return (
		<NotificationEntry
			ref={entry}
			adapter={inbox}
			href="/notifications"
			products={productNames}
			locale={language}
			// The family's own copy in the interface language (0.7.2)
			labels={NotificationEntry.labels[language]}
			// The count is read again every minute; opening the panel always reads it
			interval={60000}
			onOpen={follow}
			onView={event => {
				event.preventDefault();
				navigate('/notifications', { state: moved });
			}}
		/>
	);
}

/**
 * The full inbox, with its filter kept in the address so a reload or a shared link keeps it
 */
export function Inbox() {
	const { inbox, language, text } = usePresence();
	const follow = useFollow();
	const [parameters, setParameters] = useSearchParams();
	// Unread first, as the shared inbox opens; `?state=all` and `?product=` are kept in the address
	const state = { state: parameters.get('state') === 'all' ? ('all' as const) : ('unread' as const), product: parameters.get('product') ?? '' };
	// The inbox's filters and Mark all as read sit in the page's header, as every list's tools (L8)
	const [tools, setTools] = useState<HTMLElement | null>(null);

	return (
		<Screen template="list" width="fluid" title={text('notifications.title')} actions={<div ref={setTools} />}>
			<NotificationInbox
				tools="external"
				toolsTarget={tools}
				adapter={inbox}
				products={Notifications.products}
				locale={language}
				labels={NotificationInbox.labels[language]}
				state={state}
				onOpen={follow}
				onState={next => {
					const query = new URLSearchParams();
					if (next.state === 'all') query.set('state', 'all');
					if (next.product) query.set('product', next.product);
					setParameters(query, { replace: true });
				}}
			/>
		</Screen>
	);
}
